import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { CHART_COLORS } from '@/utils/chartColors';

interface RichContentRendererProps {
  content: string;
  data?: any;
  type?: 'text' | 'chart' | 'table' | 'video' | 'error';
}

export function RichContentRenderer({
  content,
  data,
  type = 'text',
}: RichContentRendererProps) {
  // Error display
  if (type === 'error') {
    return (
      <div className="bg-error-red/10 border border-error-red/30 rounded p-3 text-sm text-error-red">
        {content}
      </div>
    );
  }

  // Chart rendering
  if (type === 'chart' && data) {
    return (
      <div className="my-3">
        {data.chartType === 'bar' && (
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={data.data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#ECEBEA" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip />
              <Legend />
              {data.series?.map((series: any, idx: number) => (
                <Bar
                  key={series.key}
                  dataKey={series.key}
                  fill={Object.values(CHART_COLORS)[idx % Object.values(CHART_COLORS).length]}
                  name={series.name}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        )}

        {data.chartType === 'line' && (
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={data.data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#ECEBEA" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip />
              <Legend />
              {data.series?.map((series: any, idx: number) => (
                <Line
                  key={series.key}
                  type="monotone"
                  dataKey={series.key}
                  stroke={Object.values(CHART_COLORS)[idx % Object.values(CHART_COLORS).length]}
                  name={series.name}
                  strokeWidth={2}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}

        {data.chartType === 'pie' && (
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={data.data}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={80}
                label
              >
                {data.data.map((_: any, index: number) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={Object.values(CHART_COLORS)[index % Object.values(CHART_COLORS).length]}
                  />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        )}
        {content && <div className="text-sm mt-2 text-gray-300">{content}</div>}
      </div>
    );
  }

  // Table rendering
  if (type === 'table' && data) {
    return (
      <div className="my-3 overflow-x-auto">
        <table className="min-w-full border border-border-light rounded text-sm">
          <thead className="bg-gray-1">
            <tr>
              {data.headers?.map((header: string, idx: number) => (
                <th
                  key={idx}
                  className="px-4 py-2 text-left font-semibold text-dark-text border-b border-border-light"
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.rows?.map((row: any[], rowIdx: number) => (
              <tr key={rowIdx} className="hover:bg-gray-1">
                {row.map((cell: any, cellIdx: number) => (
                  <td key={cellIdx} className="px-4 py-2 border-b border-border-light">
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {content && <div className="text-sm mt-2 text-gray-300">{content}</div>}
      </div>
    );
  }

  // Video rendering
  if (type === 'video' && data?.url) {
    return (
      <div className="my-3">
        <video
          controls
          className="w-full rounded border border-border-light"
          src={data.url}
        >
          Your browser does not support the video tag.
        </video>
        {content && <div className="text-sm mt-2 text-gray-300">{content}</div>}
      </div>
    );
  }

  // Default text rendering with basic markdown-like formatting
  const formatText = (text: string) => {
    // Split by lines
    const lines = text.split('\n');
    const elements: JSX.Element[] = [];
    let listItems: string[] = [];
    let inCodeBlock = false;
    let codeLines: string[] = [];

    const flushList = () => {
      if (listItems.length > 0) {
        elements.push(
          <ul key={`list-${elements.length}`} className="list-disc pl-5 mb-2 text-sm">
            {listItems.map((item, idx) => (
              <li key={idx} className="mb-1" dangerouslySetInnerHTML={{ __html: formatInline(item) }} />
            ))}
          </ul>
        );
        listItems = [];
      }
    };

    const flushCodeBlock = () => {
      if (codeLines.length > 0) {
        elements.push(
          <pre key={`code-${elements.length}`} className="bg-gray-1 p-3 rounded overflow-x-auto text-xs mb-2">
            <code>{codeLines.join('\n')}</code>
          </pre>
        );
        codeLines = [];
      }
    };

    const formatInline = (line: string): string => {
      return line
        .replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold">$1</strong>')
        .replace(/\*(.+?)\*/g, '<em>$1</em>')
        .replace(/`(.+?)`/g, '<code class="bg-gray-1 px-1 py-0.5 rounded text-xs font-mono">$1</code>');
    };

    lines.forEach((line, idx) => {
      // Code blocks
      if (line.trim().startsWith('```')) {
        if (inCodeBlock) {
          flushCodeBlock();
          inCodeBlock = false;
        } else {
          flushList();
          inCodeBlock = true;
        }
        return;
      }

      if (inCodeBlock) {
        codeLines.push(line);
        return;
      }

      // List items
      if (line.trim().match(/^[-*]\s/)) {
        listItems.push(line.trim().substring(2));
        return;
      }

      // Flush any pending lists
      flushList();

      // Empty lines
      if (line.trim() === '') {
        return;
      }

      // Headers
      if (line.startsWith('###')) {
        elements.push(
          <h3 key={idx} className="font-semibold text-base mb-2 mt-3">
            {line.substring(3).trim()}
          </h3>
        );
        return;
      }

      if (line.startsWith('##')) {
        elements.push(
          <h2 key={idx} className="font-semibold text-lg mb-2 mt-3">
            {line.substring(2).trim()}
          </h2>
        );
        return;
      }

      // Regular paragraphs
      elements.push(
        <p
          key={idx}
          className="mb-2 text-sm leading-relaxed"
          dangerouslySetInnerHTML={{ __html: formatInline(line) }}
        />
      );
    });

    flushList();
    flushCodeBlock();

    return elements;
  };

  return <div className="prose prose-sm max-w-none">{formatText(content)}</div>;
}
